import { useMemo, useState } from "react"
import { capitalize } from "lodash"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { flatMap, groupBy } from "remeda"

import type { iconButtonVariantsType } from "@productlane/ui"
import type { Issue } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	IconButton,
	IssueStatusIcon,
	LinearIcon,
	Loader,
	Popover,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"

import { PropertyTrigger } from "@/components/property-trigger"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { issueStatusOrder } from "@/constants"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, reportServerRejection, useZero } from "@/lib/zero"

interface Props {
	issue: Issue
	iconSize?: "sm" | "default" | "lg"
	className?: string
	iconVariant?: iconButtonVariantsType
	withStatus?: boolean
}

const iconSizeMap = {
	sm: "h-3 w-3 min-h-3 min-w-3",
	default: "h-3.5 w-3.5 min-h-3.5 min-w-3.5",
	lg: "h-5 w-5 min-h-5 min-w-5",
}

export const IssueStatusSelect = ({
	issue,
	className,
	iconSize = "default",
	iconVariant = "ghost-brightness",
	withStatus = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const zero = useZero()
	const { role } = useRole()

	const { data: states } = api.linear.workflowStates.useQuery(
		{
			teamId: issue.team,
		},
		{
			cacheTime: Infinity,
			staleTime: Infinity,
			enabled: isOpen,
		},
	)

	const workflowStatesOptions = useMemo(() => {
		const groupedStates = groupBy(states || [], (state) => state.type)

		const sortedGroups = issueStatusOrder.map(
			(type) => groupedStates[type] || [],
		)

		return flatMap(sortedGroups, (group) =>
			group
				.sort((a, b) => a.position - b.position)
				.map(({ id, name, color, type }) => ({
					label: (
						<div className="ml-0.5 flex w-full items-center gap-2">
							<IssueStatusIcon
								color={color}
								className="h-3.5 min-h-3.5 w-3.5 min-w-3.5 shrink-0"
								status={type}
							/>
							<span className="flex-1 truncate">{name}</span>
							{(issue.statusId
								? issue.statusId === id
								: issue.statusName === name) && (
								<Check className="h-3 w-3 shrink-0" />
							)}
						</div>
					),
					value: id,
					statusName: name,
					color,
					type,
				})),
		)
	}, [states, issue.statusId, issue.statusName])

	return (
		<>
			<RoleCheck not={["VIEWER"]}>
				<Popover
					onOpenChange={setIsOpen}
					open={isOpen}
					className="p-0"
					content={
						workflowStatesOptions.length === 0 ? (
							<div className="flex items-center justify-center py-8">
								<Loader />
							</div>
						) : (
							<Command
								loop
								filter={(_, search, keywords) =>
									keywords?.find((keyword) =>
										keyword
											.toLocaleLowerCase()
											.includes(search.toLocaleLowerCase()),
									)
										? 1
										: 0
								}
							>
								<CommandInput autoFocus placeholder="Change status..." />
								<CommandList>
									<CommandEmpty>No matching states</CommandEmpty>
									<CommandGroup>
										{workflowStatesOptions.map(
											({ label, value, color, statusName, type }, index) => (
												<CommandItem
													keywords={[statusName, type]}
													key={`issue-status-${index}`}
													value={value}
													onSelect={async () => {
														await mutateOk(
															reportServerRejection(
																zero.mutate(
																	mutators.issues.update({
																		id: issue.id,
																		state: {
																			id: value,
																			name: statusName,
																			color,
																			type,
																		},
																	}),
																),
																(message) =>
																	toast.error({
																		title: `${statusName} was not saved in Linear`,
																		description: message,
																		icon: LinearIcon,
																	}),
															),
														)
														analytics.track("issue-status-update")
														setIsOpen(false)
													}}
												>
													<div className="text-md flex w-full items-center justify-between -space-y-0.5">
														<div className="flex w-full truncate">
															<div className="w-full flex-none font-medium">
																{label}
															</div>
														</div>
													</div>
												</CommandItem>
											),
										)}
									</CommandGroup>
								</CommandList>
							</Command>
						)
					}
				>
					{withStatus ? (
						<PropertyTrigger
							tooltip="Change status"
							className={className}
							icon={
								<IssueStatusIcon
									className="h-3.5 w-3.5 flex-none overflow-visible"
									status={issue?.status || ""}
									color={issue?.statusColor || ""}
								/>
							}
						>
							{capitalize(issue.statusName || "")}
						</PropertyTrigger>
					) : (
						<div className={cn("group", className)}>
							<Tooltip label="Change status" shouldWrapChildren>
								<div className="flex flex-row items-center gap-1">
									<IconButton
										size="md"
										variant={iconVariant}
										onClick={(e) => {
											e.preventDefault()
											setIsOpen(!isOpen)
										}}
									>
										<IssueStatusIcon
											className={cn(
												"overflow-visible transition-all",
												iconSizeMap[iconSize],
											)}
											status={issue?.status || ""}
											color={issue?.statusColor || ""}
										/>
									</IconButton>
								</div>
							</Tooltip>
						</div>
					)}
				</Popover>
			</RoleCheck>
			{role === "VIEWER" &&
				(withStatus ? (
					<PropertyTrigger
						className={className}
						icon={
							<IssueStatusIcon
								className="h-3.5 w-3.5 flex-none overflow-visible"
								status={issue?.status || ""}
								color={issue?.statusColor || ""}
							/>
						}
					>
						{capitalize(issue.statusName || "")}
					</PropertyTrigger>
				) : (
					<IssueStatusIcon
						className={cn("overflow-visible", iconSizeMap[iconSize])}
						status={issue?.status || ""}
						color={issue?.statusColor || ""}
					/>
				))}
		</>
	)
}
